import React, { type FC, useEffect } from 'react';

import { FieldType } from '@/api/types';
import { useTemplateEditorStore } from '../store';
import { FieldsPanelView, LayerType } from '../types';
import { FieldsListView } from './fields-list-view';
import { ImageFieldEditView } from './image-field-edit-view';
import { ImageLayerEditView } from './image-layer-edit-view';
import { ImageLayerLinkView } from './image-layer-link-view';
import { TextFieldEditView } from './text-field-edit-view';
import { TextLayerEditView } from './text-layer-edit-view';
import { TextLayerLinkView } from './text-layer-link-view';

export const FieldsPanel: FC = () => {
    const editedFieldId = useTemplateEditorStore((s) => s.editedFieldId);
    const fields = useTemplateEditorStore((s) => s.template?.latestVersion.fields);
    const selectedLayer = useTemplateEditorStore((s) => s.selectedLayer);
    const view = useTemplateEditorStore((s) => s.fieldsPanelView);
    const setView = useTemplateEditorStore((s) => s.setFieldsPanelView);

    const setDefaultMode = () => {
        setView(FieldsPanelView.Default);
    };

    const setCreateMode = (type: FieldType) => {
        switch (type) {
            case FieldType.Text:
                setView(FieldsPanelView.TextFieldEdit);
                break;
            case FieldType.Image:
                setView(FieldsPanelView.ImageFieldEdit);
                break;
        }
    };

    const setLinkMode = (type: FieldType) => {};

    let viewContent = <></>;
    let redirectView = view;
    switch (view) {
        case FieldsPanelView.TextFieldEdit:
            viewContent = <TextFieldEditView field={fields?.find((f) => f.id === editedFieldId) || null} />;
            break;
        case FieldsPanelView.TextLayerEdit:
            if (selectedLayer) {
                viewContent = <TextLayerEditView layer={selectedLayer} />;
            } else {
                redirectView = FieldsPanelView.Default;
            }
            break;
        case FieldsPanelView.TextLayerLink:
            if (selectedLayer) {
                const linkedField = fields
                    ? fields.find((f) => f.linkedLayers?.find((l) => l.layerUuid === selectedLayer.id))
                    : null;
                if (!linkedField) {
                    viewContent = <TextLayerLinkView layer={selectedLayer} />;
                    break;
                }
            }
            redirectView = FieldsPanelView.Default;
            break;
        case FieldsPanelView.ImageFieldEdit:
            viewContent = <ImageFieldEditView onClose={setDefaultMode} />;
            break;
        case FieldsPanelView.ImageLayerEdit:
            if (selectedLayer) {
                viewContent = (
                    <ImageLayerEditView
                        layer={selectedLayer}
                        onClose={setDefaultMode}
                        onCreateField={() => setCreateMode(FieldType.Image)}
                        onLinkField={() => setLinkMode(FieldType.Image)}
                    />
                );
            } else {
                redirectView = FieldsPanelView.Default;
            }
            break;
        case FieldsPanelView.ImageLayerLink:
            viewContent = (
                <ImageLayerLinkView onClose={setDefaultMode} onCreateField={() => setCreateMode(FieldType.Image)} />
            );
            break;
        default:
            if (selectedLayer) {
                switch (selectedLayer.type) {
                    case LayerType.Text:
                        redirectView = FieldsPanelView.TextLayerEdit;
                        break;
                    case LayerType.Image:
                        redirectView = FieldsPanelView.ImageLayerEdit;
                        break;
                }
            } else {
                // Default view
                viewContent = <FieldsListView />;
            }
            break;
    }

    useEffect(() => {
        setView(redirectView);
    }, [redirectView, setView]);
    return viewContent;
};
